import React, { useState, useMemo } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Inbox, Copy, Flame, AlertTriangle, Clock, Zap, Ban } from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { v: "urgent",       l: "Urgent",       icon: AlertTriangle, color: "text-red-500 border-red-700 bg-red-500/5" },
  { v: "needs_reply",  l: "Needs reply",  icon: Flame,         color: "text-yellow-400 border-yellow-700 bg-yellow-400/5" },
  { v: "easy",         l: "Easy",         icon: Zap,           color: "text-green-400 border-green-700 bg-green-400/5" },
  { v: "in_progress",  l: "In progress",  icon: Clock,         color: "text-blue-400 border-blue-700 bg-blue-400/5" },
  { v: "skip",         l: "Skip",         icon: Ban,           color: "text-zinc-600 border-zinc-800 bg-zinc-900/30" },
];

export default function InboxParser() {
  const [dump, setDump] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeBucket, setActiveBucket] = useState("all");

  const parse = async () => {
    if (!dump.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/api/inbox/parse", { dump });
      setRes(r.data);
      toast.success(`Triaged ${r.data.conversations?.length || 0} threads`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const grouped = useMemo(() => {
    const g = { urgent: [], needs_reply: [], easy: [], in_progress: [], skip: [] };
    (res?.conversations || []).forEach((c) => {
      const b = g[c.bucket] ? c.bucket : "needs_reply";
      g[b].push(c);
    });
    return g;
  }, [res]);

  const visible = useMemo(() => {
    if (!res) return [];
    if (activeBucket === "all") return res.conversations || [];
    return grouped[activeBucket] || [];
  }, [res, grouped, activeBucket]);

  return (
    <AppLayout title="Inbox parser" overline="090 · X / TWITTER / IG DMs · TRIAGE v2">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <section className="border border-zinc-900 bg-zinc-950 p-6 fade-up h-fit sticky top-6" data-testid="inbox-input-panel">
          <div className="overline mb-3">Paste your raw DM dump</div>
          <textarea value={dump} onChange={(e) => setDump(e.target.value)} rows={18}
            placeholder="Paste full inbox (X, IG, or any). The Co-Pilot will triage every thread into 5 buckets and generate replies."
            data-testid="inbox-input"
            className="w-full bg-black border border-zinc-900 text-white p-3 text-sm focus:outline-none focus:border-[color:var(--gio-primary)] font-mono-ui" />

          <button onClick={parse} disabled={busy || !dump.trim()} data-testid="inbox-parse-button"
            className="btn-primary mt-4 w-full flex items-center justify-center gap-2 disabled:opacity-40">
            <Inbox size={16} /> {busy ? "Triaging..." : "Triage inbox"}
          </button>

          {res?.stats && (
            <div className="mt-6 pt-4 border-t border-zinc-900 space-y-2" data-testid="inbox-stats">
              <div className="overline mb-2">Breakdown</div>
              {BUCKETS.map((b) => (
                <button key={b.v} onClick={() => setActiveBucket(activeBucket === b.v ? "all" : b.v)} data-testid={`inbox-bucket-filter-${b.v}`}
                  className={`flex justify-between items-center w-full py-2 px-3 text-xs border transition ${activeBucket === b.v ? b.color + " font-bold" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>
                  <span className="flex items-center gap-2"><b.icon size={12} /> {b.l}</span>
                  <span className="font-mono-ui">{res.stats[b.v] ?? 0}</span>
                </button>
              ))}
              {activeBucket !== "all" && (
                <button onClick={() => setActiveBucket("all")} data-testid="inbox-bucket-clear" className="text-[color:var(--gio-primary)] text-xs font-mono-ui uppercase tracking-wider mt-2">← Show all</button>
              )}
            </div>
          )}
        </section>

        <section className="space-y-3" data-testid="inbox-results">
          {!res && !busy && (
            <div className="border border-dashed border-zinc-900 p-12 text-center text-zinc-600 text-sm">
              Triaged conversations with suggested replies will land here — grouped by urgency.
            </div>
          )}
          {busy && <div className="overline text-[color:var(--gio-primary)] p-6 flex items-center gap-2"><div className="animate-pulse">READING YOUR INBOX...</div></div>}

          {res?.overall_note && (
            <div className="border border-[color:var(--gio-primary)]/40 bg-zinc-950 p-4 text-sm text-[color:var(--gio-primary)] fade-up" data-testid="inbox-strategy-note">
              <span className="overline mr-2">STRATEGY</span>{res.overall_note}
            </div>
          )}

          {visible.map((c, i) => {
            const bucket = BUCKETS.find(b => b.v === c.bucket) || BUCKETS[1];
            return (
              <div key={i} className={`border p-5 fade-up ${bucket.color}`} data-testid={`conv-${i}`}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="text-white text-sm font-medium">{c.sender}</div>
                    <div className="text-zinc-500 text-[11px] mt-0.5">{c.summary}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono-ui uppercase tracking-wider border px-2 py-0.5 flex items-center gap-1 ${bucket.color}`}>
                      <bucket.icon size={10} /> {bucket.l}
                    </span>
                    {c.priority && <span className="text-[10px] font-mono-ui uppercase tracking-wider text-zinc-400 border border-zinc-700 px-2 py-0.5">{c.priority}</span>}
                    {c.intent && <span className="text-[10px] font-mono-ui uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-0.5">{c.intent}</span>}
                  </div>
                </div>

                {c.action_hint && (
                  <div className="text-[color:var(--gio-secondary)] text-[12px] mb-3 font-mono-ui uppercase tracking-wider">
                    → {c.action_hint}
                  </div>
                )}

                {Array.isArray(c.suggested_replies) && c.suggested_replies.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {c.suggested_replies.map((r, ri) => (
                      <div key={ri} className="border-l-2 border-[color:var(--gio-primary)] pl-3 text-white text-sm flex justify-between items-start gap-3 group">
                        <div>
                          <div className="overline mb-1">{r.tone?.toUpperCase()}</div>
                          <div>{r.text}</div>
                        </div>
                        <button onClick={() => copy(r.text)} data-testid={`conv-copy-${i}-${ri}`}
                          className="text-zinc-500 hover:text-[color:var(--gio-primary)] shrink-0 opacity-0 group-hover:opacity-100 transition">
                          <Copy size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Legacy single-reply fallback */}
                {!c.suggested_replies && c.suggested_reply && (
                  <div className="border-l-2 border-[color:var(--gio-primary)] pl-3 text-white text-sm flex justify-between items-start gap-3 mt-3">
                    <div>
                      <div className="overline mb-1">SUGGESTED REPLY</div>
                      <div>{c.suggested_reply}</div>
                    </div>
                    <button onClick={() => copy(c.suggested_reply)} data-testid={`conv-copy-${i}`} className="text-zinc-500 hover:text-[color:var(--gio-primary)] shrink-0"><Copy size={14} /></button>
                  </div>
                )}
              </div>
            );
          })}

          {res && !visible.length && (
            <div className="border border-dashed border-zinc-900 p-8 text-center text-zinc-600 text-sm">
              No threads in this bucket.
            </div>
          )}

          {res && !res.conversations?.length && res.raw && (
            <pre className="border border-zinc-900 bg-zinc-950 p-4 text-xs text-zinc-400 whitespace-pre-wrap">{res.raw}</pre>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
